import * as openpgp from "openpgp";
import companyKeysService from "api/companyKeysService";
import { ICompanyKey, ICompanyKeysView } from "dto/ICompanyKey";
import { ISingleBinaryKeys } from "./GenerateSingleBinary";
import { generateSingleBinaryFromMultiplePublicKeys } from "./generateSingleBinaryFromMultiplePublicKeys";
import { readKeyMaterial } from "./readPublicKey";

// JEDYNE wejście ZAPISU nowej treści w modelu kluczy firmowych (FAZA 3,
// docs/model-kluczy-firmowych.md, sekcja 7). Zastępuje kilkanaście miejsc, które owijały
// klucz obiektu kluczem publicznym AKTYWNEGO PROFILU (dawny kontekst profilu, usunięty
// w F8) albo kluczem POŚREDNIM nadawcy (`myKeys.publicBase64Key`).
//
// PLIK SCALONY z tymczasową wersją Zadania D (ścieżki tworzenia sprawy), która powstała
// zanim ten strumień opublikował swoją — stąd zachowane nazwy `encryptForCompany`,
// `getActiveCompanyPublicKeyBase64` i `CompanyPublicKeyMissingError`.
//
// Inwariant I1: treść szyfruje ZAWSZE klucz obiektu, a klucz obiektu owija klucz FIRMY.
// Dzięki temu każdy członek firmy z grantem czyta wszystko, co firma zapisała, bez ani
// jednego udostępnienia — i dzięki temu rotacja ma co przewijać (65 tys. małych blobów,
// nie 181 tys. plików w S3).
//
// FAIL-CLOSED (polecenie fazy). Gdy sesja nie ma aktywnego klucza firmy, te funkcje RZUCAJĄ
// `CompanyKeyMissingError`. NIE MA cichego powrotu do klucza profilu: taki powrót zapisałby
// treść, której druga osoba w firmie nie otworzy, a objawem byłaby „pusta treść u części
// użytkowników" wykryta tygodnie później — dokładnie ta klasa awarii, którą ta przebudowa
// ma zlikwidować. Brak klucza to stan JAWNY: użytkownik widzi błąd zapisu, administrator
// wydaje mu klucz (POST /workspace/member/key), zapis rusza.
//
// Klucz publiczny firmy trzyma MIRROR MODUŁOWY, a nie kontekst Reacta, bo szyfrują też
// zwykłe funkcje wołane poza drzewem (utils/uploadCompanyEncryptedFile, hooki mutacji
// w api/*Service). Mirror karmi App.tsx przy starcie sesji; gdyby nie zdążył (grant wydany
// w trakcie sesji, wyścig przy pierwszym zapisie) — funkcje asynchroniczne DOCIĄGAJĄ klucz
// z `GET /workspace/key` same. To nie jest fallback na inny klucz, tylko późniejsze
// pobranie TEGO SAMEGO.

/** Brak aktywnego klucza firmy w sesji. Jawny typ — łapany przez bramki i telemetrię. */
export class CompanyKeyMissingError extends Error {
  constructor(
    message = "Firma nie ma jeszcze aktywnego klucza szyfrującego — nie można zaszyfrować nowej treści. Poproś administratora firmy o wydanie klucza.",
  ) {
    super(message);
    this.name = "CompanyKeyMissingError";
  }
}

/** Nazwa z tymczasowej wersji Zadania D — ten sam błąd, zachowana dla wołających. */
export const CompanyPublicKeyMissingError = CompanyKeyMissingError;

/** Tożsamość aktywnej generacji klucza firmy (bez materiału prywatnego — I4). */
export interface IActiveCompanyKey {
  /** Workspaces.Id — trafia na ObjectKeys.WorkspaceId po stronie BE. */
  WorkspaceId: string;
  /** WorkspaceKeys.Id aktywnej generacji — trafia na ObjectKeys.WorkspaceKeyId. */
  WorkspaceKeyId: string;
  Generation: number;
  /** Klucz publiczny firmy, base64 binarnego packetlistu (I3). */
  PublicKeyBase64: string;
}

let active: (IActiveCompanyKey & { key: openpgp.key.Key }) | null = null;

/** Trwające dociąganie klucza — żeby N równoległych zapisów nie zrobiło N zapytań. */
let inFlight: Promise<void> | null = null;

/**
 * Ustawia aktywną generację klucza firmy. Parsuje materiał OD RAZU (readKeyMaterial rzuca
 * przy śmieciu), żeby błędny klucz wywalił się przy primingu — jednym jawnym błędem — a nie
 * przy każdym zapisie z osobna.
 */
export const setActiveCompanyKey = async (
  info: IActiveCompanyKey | null | undefined,
): Promise<void> => {
  if (!info?.PublicKeyBase64) {
    active = null;
    return;
  }

  active = { ...info, key: await readKeyMaterial(info.PublicKeyBase64) };
};

/** Czyści mirror klucza firmy — wylogowanie, odebranie grantu, zmiana firmy. */
export const clearActiveCompanyKey = (): void => {
  active = null;
};

/** Czy sesja ma już w pamięci czym zaszyfrować nową treść (bez sięgania do sieci). */
export const hasActiveCompanyKey = (): boolean => active !== null;

/** Tożsamość aktywnej generacji BEZ dociągania (diagnostyka, bramki UI). Null gdy brak. */
export const peekActiveCompanyKey = (): IActiveCompanyKey | null => {
  if (!active) return null;
  const { key, ...info } = active;
  void key;
  return info;
};

/**
 * Wybiera AKTYWNĄ generację z odpowiedzi GET /workspace/key.
 *
 * Pierwszeństwo ma `ActiveKeyId` (to jest źródło prawdy: `Workspaces.ActiveWorkspaceKeyId`).
 * Gdy go nie ma — najwyższa NIEWYCOFANA generacja. Zapis zawsze idzie na jedną, konkretną
 * generację; czytamy natomiast wszystkimi, na które mamy grant.
 */
export const pickActiveCompanyKey = (
  view: ICompanyKeysView | null | undefined,
): (ICompanyKey & { WorkspaceId: string }) | null => {
  const keys = (view?.Keys ?? []).filter((k) => !!k?.PublicKey);
  if (!keys.length) return null;

  const byId = view?.ActiveKeyId
    ? keys.find((k) => k.Id === view.ActiveKeyId)
    : undefined;

  const chosen =
    byId ??
    keys
      .filter((k) => !k.RetiredDate)
      .sort((a, b) => b.Generation - a.Generation)[0];

  if (!chosen) return null;

  return {
    ...chosen,
    WorkspaceId: chosen.WorkspaceId || (view?.WorkspaceId ?? ""),
  };
};

/** Dociąga aktywną generację z BE (jednokrotnie, także przy N równoległych zapisach). */
const fetchActiveCompanyKey = async (): Promise<void> => {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const { data } = await companyKeysService.getCompanyKeys();
    const chosen = pickActiveCompanyKey(data);

    await setActiveCompanyKey(
      chosen
        ? {
            WorkspaceId: chosen.WorkspaceId,
            WorkspaceKeyId: chosen.Id,
            Generation: chosen.Generation,
            PublicKeyBase64: chosen.PublicKey,
          }
        : null,
    );
  })();

  try {
    await inFlight;
  } finally {
    inFlight = null;
  }
};

/**
 * Aktywna generacja klucza firmy — z mirrora, a gdy pusty, dociągnięta z BE.
 * RZUCA `CompanyKeyMissingError`, gdy konto nie ma firmy albo firma nie ma jeszcze
 * wydanego klucza.
 */
const requireActive = async (): Promise<
  IActiveCompanyKey & { key: openpgp.key.Key }
> => {
  if (active) return active;

  try {
    await fetchActiveCompanyKey();
  } catch (error) {
    throw new CompanyKeyMissingError(
      `Nie udało się pobrać klucza firmy: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }

  if (!active) throw new CompanyKeyMissingError();

  return active;
};

/** Tożsamość aktywnej generacji (WorkspaceId + WorkspaceKeyId). RZUCA przy braku. */
export const getActiveCompanyKey = async (): Promise<IActiveCompanyKey> => {
  const { key, ...info } = await requireActive();
  void key;
  return info;
};

/** Klucz publiczny firmy jako base64 — do pul multi-recipient. RZUCA przy braku. */
export const getCompanyPublicKeyBase64 = async (): Promise<string> =>
  (await requireActive()).PublicKeyBase64;

/** Nazwa z tymczasowej wersji Zadania D — ta sama funkcja. */
export const getActiveCompanyPublicKeyBase64 = getCompanyPublicKeyBase64;

/** Klucz publiczny firmy (sparsowany). RZUCA przy braku. */
export const getCompanyPublicKey = async (): Promise<openpgp.key.Key> =>
  (await requireActive()).key;

/**
 * Klucz publiczny firmy w postaci `KeyResult` — tego kształtu oczekują handleCreate
 * i generateSingleBinary (`encryptKey.keys[0]`). RZUCA przy braku.
 */
export const getCompanyPublicKeyResult =
  async (): Promise<openpgp.key.KeyResult> =>
    ({
      keys: [await getCompanyPublicKey()],
      err: undefined,
    } as openpgp.key.KeyResult);

/**
 * Nowy klucz obiektu owinięty kluczem publicznym firmy — bezpośredni zamiennik dawnego
 * `generateSingleBinary(<klucz publiczny aktywnego profilu>)`. RZUCA przy braku klucza firmy.
 */
export const encryptForCompany = async (): Promise<ISingleBinaryKeys> =>
  generateSingleBinaryFromMultiplePublicKeys([
    await getCompanyPublicKeyBase64(),
  ]);

/**
 * Nowy klucz obiektu owinięty kluczem firmy ORAZ kluczami stron zewnętrznych (odbiorca
 * przesyłki, uczestnicy czatu, wsparcie platformy w helpdesku — D3). Klucz firmy jest
 * ZAWSZE pierwszy i zawsze obecny; pozycje puste są odrzucane, nieczytelne RZUCAJĄ
 * (fail-closed puli, patrz generateSingleBinaryFromMultiplePublicKeys).
 */
export const encryptForCompanyAndRecipients = async (
  recipients: (string | null | undefined)[],
): Promise<ISingleBinaryKeys> =>
  generateSingleBinaryFromMultiplePublicKeys([
    await getCompanyPublicKeyBase64(),
    ...(recipients ?? []).filter((r): r is string => !!r?.length),
  ]);

export default encryptForCompany;
